import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getLoginLogs, getSystemLogs } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  FileText,
  LogIn,
  Loader2,
  Monitor,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

export const LogsPage = () => {
  const navigate = useNavigate();
  const { isLeader } = useAuth();
  
  const [loginLogs, setLoginLogs] = useState([]);
  const [systemLogs, setSystemLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLeader()) {
      navigate('/dashboard');
      return;
    }
    fetchLogs();
  }, [isLeader, navigate]);

  const fetchLogs = async () => {
    try {
      const [loginRes, systemRes] = await Promise.all([
        getLoginLogs(),
        getSystemLogs()
      ]);
      setLoginLogs(loginRes.data);
      setSystemLogs(systemRes.data);
    } catch (error) {
      toast.error('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncate = (str, len = 50) => {
    if (!str) return '-';
    return str.length > len ? str.substring(0, len) + '...' : str;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-imperial" />
      </div>
    );
  }

  return (
    <div data-testid="logs-page" className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-imperial">System Logs</h1>
        <p className="font-body text-muted-foreground mt-1">
          Monitor login activity and system events
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="login">
        <TabsList>
          <TabsTrigger value="login" data-testid="login-logs-tab">
            <LogIn className="w-4 h-4 mr-2" />
            Login Logs
          </TabsTrigger>
          <TabsTrigger value="system" data-testid="system-logs-tab">
            <FileText className="w-4 h-4 mr-2" />
            System Logs
          </TabsTrigger>
        </TabsList>

        {/* Login Logs Tab */}
        <TabsContent value="login">
          <Card className="card-luxury">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User Email</TableHead>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Device</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loginLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No login logs yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      loginLogs.map((log) => (
                        <TableRow key={log.id} data-testid={`login-log-${log.id}`}>
                          <TableCell className="font-medium">{log.user_email}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              {formatDateTime(log.timestamp)}
                            </div>
                          </TableCell>
                          <TableCell>{log.ip_address}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Monitor className="w-4 h-4 text-muted-foreground" />
                              <span className="truncate max-w-xs">{truncate(log.device_type, 40)}</span>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Logs Tab */}
        <TabsContent value="system">
          <Card className="card-luxury">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Type</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {systemLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                          No system logs yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      systemLogs.map((log) => (
                        <TableRow key={log.id} data-testid={`system-log-${log.id}`}>
                          <TableCell>
                            <Badge className={
                              log.log_type.includes('ERROR') 
                                ? 'bg-red-50 text-red-700 border border-red-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }>
                              {log.log_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{log.message}</TableCell>
                          <TableCell className="max-w-xs truncate">{truncate(log.details, 50)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Clock className="w-4 h-4 text-muted-foreground" />
                              {formatDateTime(log.timestamp)}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LogsPage;
